// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.
// TODO. Supports stop/limit pairs so far.
// Refactor and delete OCOCustomOrdersEdit class.
// Currently supports Stop/Limit pair only.

import { GeneralSettings } from "../../../../Utils/GeneralSettings/GeneralSettings.js"
import { OperationType } from "../../../../Utils/Trading/OperationType.ts"
import { OrderEditBaseUtils } from "../../../../Utils/Trading/OrderEditBaseUtils.js"
import { OrderEditUpdateData } from "../../../../Utils/Trading/OrderEditUpdateData.js"
import { OrderType } from "../../../../Utils/Trading/OrderType.js"
import { OrderUtils } from "../../../../Utils/Trading/OrderUtils.js"
import { DynProperty } from "../../../DynProperty.js"
import { OrderExecutorUtils } from "../../../Trading/OrderExecutorUtils.js"
import { Resources } from "../../../properties/Resources.js"
import { SLTPEdit } from "../SLTPEdit.js"
import { OrderEditBase } from "./OrderEditBase.js"


export let OCOOrderEdit = function (data)
{
    OrderEditBase.call(this, data)
    this.stopPrice = null
    this.limitPrice = null

    this.needSetDefaultStopPrice = false
    this.needSetDefaultLimitPrice = false

    this.sltp = new SLTPEdit(data.dataCache, true)
}

OCOOrderEdit.prototype = Object.create(OrderEditBase.prototype)

OCOOrderEdit.prototype.getParameterNameArray = function ()
{
    return [
        OrderEditBaseUtils.STOP_PRICE_PARAM,
        OrderEditBaseUtils.LIMIT_PRICE_PARAM,
        OrderEditBaseUtils.POSITION_SIZING_PARAM,
        OrderEditBaseUtils.SLTP_PARAM
    ]
}

// TODO. Refactor. Same as OrderTypeBase.Id function.
OCOOrderEdit.prototype.getOrderTypeId = function ()
{
    return OrderType.OCO
}

// TODO. Refactor. Use error/warning dicts for sltp.
// OCOOrderEdit.prototype.valid = function ()
// {
//     return OrderEditBase.prototype.valid.call(this) &&
//         this.sltp.valid()
// }

// #region Parameters' DynProperty Generators

OCOOrderEdit.prototype.createPriceDynProperty = function (price, localizationKey, isStopPrice = false)
{
    let dpName = OrderEditBase[isStopPrice ? 'STOP_PRICE_PARAM' : 'LIMIT_PRICE_PARAM']
    let dp = new DynProperty(dpName)
    dp.type = DynProperty.DOUBLE

    let ins = this.instrument
    if (ins)
    {
        dp.minimalValue = ins.PointSize
        dp.maximalValue = 999999999
        dp.decimalPlaces = ins.Precision
        dp.increment = ins.PointSize
    }

    let insDefSettings = GeneralSettings.InsDefSettingsStorage.GetInstrumentSettings(ins)
    if (insDefSettings)
        dp.increment = ins.PointSize * insDefSettings.PriceIncrementTicks

    dp.value = price
    if (dp.value === null || isNaN(dp.value))
        dp.value = dp.minimalValue

    dp.localizationKey = localizationKey

    return dp
}

OCOOrderEdit.prototype.toDynProperty_stopPrice = function ()
{
    return this.createPriceDynProperty(
        this.stopPrice,
        'panel.newOrderEntry.stopPricePanel', true)
}

OCOOrderEdit.prototype.toDynProperty_limitPrice = function ()
{
    return this.createPriceDynProperty(
        this.limitPrice,
        'panel.newOrderEntry.limitPricePanel')
}

OCOOrderEdit.prototype.toDynProperty_sltp = function ()
{
    return this.sltp.getDynProperty(this.getTradingData())
}

// #endregion Parameters' DynProperty Generators

// #region Update Parameter Handlers

OCOOrderEdit.prototype.update_sltp = function (updateData)
{
    return this.sltp.update(updateData, this.getTradingData())
}

// TODO. Refactor. Duplicate of update_limitPrice.
OCOOrderEdit.prototype.update_stopPrice = function (updateData)
{
    let parameterChanged = false

    let dp = updateData.dp
    if (dp && this.stopPrice !== dp.value)
    {
        this.stopPrice = dp.value
        parameterChanged |= true
    }

    let newInstrument = null
    let newQuote = null
    let sideChanged = false

    let newTradingDataDict = updateData.newTradingDataDict
    if (newTradingDataDict)
    {
        newInstrument = newTradingDataDict.instrument
        newQuote = newTradingDataDict.quote
        sideChanged = 'side' in newTradingDataDict
    }

    if (newInstrument)
    {
        this.stopPrice = null
        this.needSetDefaultStopPrice = true
        parameterChanged |= true
    }

    if (this.needSetDefaultStopPrice && newQuote)
    {
        this.needSetDefaultStopPrice = false
        this.stopPrice = newQuote.BidSpread_SP_Ins(this.instrument.DataCache.GetSpreadPlan(this.account), this.instrument);
        parameterChanged |= true
    }

    if (newQuote || sideChanged || parameterChanged)
    {
        parameterChanged |= this.tryUpdateWarning_stopPrice(
            this.stopPrice,
            this.quote)
    }

    return parameterChanged
}

// TODO. Refactor. Duplicate of update_stopPrice.
OCOOrderEdit.prototype.update_limitPrice = function (updateData)
{
    let parameterChanged = false

    let dp = updateData.dp
    if (dp && this.limitPrice !== dp.value)
    {
        this.limitPrice = dp.value
        parameterChanged |= true
    }

    let newInstrument = null
    let newQuote = null
    let sideChanged = false

    let newTradingDataDict = updateData.newTradingDataDict
    if (newTradingDataDict)
    {
        newInstrument = newTradingDataDict.instrument
        newQuote = newTradingDataDict.quote
        sideChanged = 'side' in newTradingDataDict
    }

    if (newInstrument)
    {
        this.limitPrice = null
        this.needSetDefaultLimitPrice = true
        parameterChanged |= true
    }

    if (this.needSetDefaultLimitPrice && newQuote)
    {
        this.needSetDefaultLimitPrice = false
        this.limitPrice = newQuote.BidSpread_SP_Ins(this.instrument.DataCache.GetSpreadPlan(this.account), this.instrument)
        parameterChanged |= true
    }

    if (newQuote || sideChanged || parameterChanged)
    {
        parameterChanged |= this.tryUpdateWarning_limitPrice(
            this.limitPrice,
            this.quote)
    }

    return parameterChanged
}

// TODO. Duplicate of tryUpdateWarning_limitPrice.
OCOOrderEdit.prototype.tryUpdateWarning_stopPrice = function (stopPrice, quote)
{
    if (!quote || !stopPrice)
        return false

    let sp = this.instrument.DataCache.GetSpreadPlan(this.account)
    let ask = quote.AskSpread_SP_Ins(sp, this.instrument)
    let bid = quote.BidSpread_SP_Ins(sp, this.instrument)

    let buy = this.buy()

    if (this.firstOrderType || this.limitPrice == null)
        return false    // todo warning for custom OCO

    let newWarning =
        buy && stopPrice < ask
            ? 'general.trading.stopBuyMoreAsk'
            : !buy && stopPrice > bid
                ? 'general.trading.stopSellLessBid'
                : null

    return this.setParameterWarning(OrderEditBaseUtils.STOP_PRICE_PARAM, newWarning)
}

// TODO. Duplicate of tryUpdateWarning_stopPrice.
OCOOrderEdit.prototype.tryUpdateWarning_limitPrice = function (limitPrice, quote)
{
    if (!quote || !limitPrice)
        return false

    let sp = this.instrument.DataCache.GetSpreadPlan(this.account)
    let ask = quote.AskSpread_SP_Ins(sp, this.instrument)
    let bid = quote.BidSpread_SP_Ins(sp, this.instrument)

    let buy = this.buy()

    if (this.firstOrderType)
        return false    // todo warning for custom OCO

    let newWarning =
        buy && limitPrice > ask
            ? 'general.trading.limitBuyLessAsk'
            : !buy && limitPrice < bid
                ? 'general.trading.limitSellMoreBid'
                : null

    return this.setParameterWarning(OrderEditBaseUtils.LIMIT_PRICE_PARAM, newWarning)
}

// #endregion Update Parameter Handlers

// #region Raw Values

OCOOrderEdit.prototype.toRawValue_sltp = function ()
{
    return this.sltp.getRawValue(this.getTradingData())
}

OCOOrderEdit.prototype.toRawValue_stopPrice = function ()
{
    return this.stopPrice
}

OCOOrderEdit.prototype.toRawValue_limitPrice = function ()
{
    return this.limitPrice
}

// #endregion Raw Values

OCOOrderEdit.prototype.validateParameters = function ()
{
    let updatedParamNameDict = {}

    if (this.sltp.validate(null, null, this.getTradingData()))
        updatedParamNameDict[OrderEditBaseUtils.SLTP_PARAM] = true

    updatedParamNameDict[OrderEditBaseUtils.POSITION_SIZING_PARAM] = true

    return updatedParamNameDict
}

OCOOrderEdit.prototype.getDataForRequest = function ()
{
    let data = OrderEditBase.prototype.getDataForRequest.call(this)

    data.useStopLimitInsteadStop = this.useStopLimitInsteadStop()
    if (data.useStopLimitInsteadStop)
        data.limitPriceForStopLimitOrder = this.getLimitPriceWithDefaultOffset(this.toRawValue_stopPrice())

    return data
}

OCOOrderEdit.prototype.useStopLimitInsteadStop = function ()    // для удобства вызова, чтоб не лазить глубоко в сеттинги
{
    return GeneralSettings.TradingDefaults.UseStopLimitInsteadStop
}

OCOOrderEdit.prototype.getConfirmationText = function ()
{
    let firstOrderType = this.firstOrderType != undefined ? OrderUtils.getOrderTypeLocalizationKey(this.firstOrderType) : 'Limit',
        secondOrderType = this.secondOrderType != undefined ? OrderUtils.getOrderTypeLocalizationKey(this.secondOrderType) : (this.useStopLimitInsteadStop() ? 'Stop Limit' : 'Stop'),
        firstOrderTif = this.firstOrderTif != undefined ? this.firstOrderTif : this.tif,
        secondOrderTif = this.secondOrderTif != undefined ? this.secondOrderTif : this.tif,
        firstSideIsBuy = this.firstOrderSide != undefined ? this.firstOrderSide === OperationType.Buy : this.buy(),
        secondSideIsBuy = this.secondOrderSide != undefined ? this.secondOrderSide === OperationType.Buy : this.buy()

    let firstPartPriceArray = [this.toRawValue_limitPrice()]
    if (this.firstOrderType == OrderType.StopLimit && this.useStopLimitInsteadStop())
        firstPartPriceArray.push(this.getLimitPriceWithDefaultOffset(this.toRawValue_limitPrice(), firstSideIsBuy))

    let firstPart = OrderExecutorUtils.buildOrderEditConfirmationText(
        firstOrderType,
        firstOrderTif,
        null,
        this.instrument,
        this.quantity,
        firstSideIsBuy,
        firstPartPriceArray,
        '',
        null,
        this.productType,
        firstOrderType,
        null,
        this.getLeverageValue())

    firstPart = firstPart.substr(0, firstPart.length - 1) + ','

    let stopPartPriceArray = [this.stopPrice]
    if ((this.secondOrderType === undefined || this.secondOrderType == OrderType.StopLimit) && this.useStopLimitInsteadStop())
        stopPartPriceArray.push(this.getLimitPriceWithDefaultOffset(this.toRawValue_stopPrice(), secondSideIsBuy))

    let secondPart = OrderExecutorUtils.buildOrderEditConfirmationText(
        secondOrderType,
        secondOrderTif,
        null,
        this.instrument,
        this.quantity,
        secondSideIsBuy,
        stopPartPriceArray,
        '',
        null,
        this.productType,
        secondOrderType,
        null,
        this.getLeverageValue())

    let sltpPart = this.sltp.getConfirmationText(this.getTradingData())

    return Resources.getResource('OCO') +
        ' ' +
        Resources.getResource('property.protrader.showOrders') +
        ' ' +
        Resources.getResource('general.trading.for') +
        ' ' +
        this.account.toString() +
        ':\n' +
        firstPart +
        '\n' +
        secondPart +
        (sltpPart ? '\n' + sltpPart : '')
}

// TODO. UGLY. Refactor. details are at the top OrderEditBase.js
OCOOrderEdit.prototype.setSLTP = function (sltpHolder)
{
    let dp = this.sltp.createDynPropertyForRawSLTP(
        sltpHolder,
        this.getTradingData(),
        null,
        null)

    this.updateParameters(new OrderEditUpdateData({ sltp: dp }))
}

OCOOrderEdit.prototype.setBasePrice = function (price)
{
    let dpLimitPrice = this.createDynPropertyFromParameter(OrderEditBaseUtils.LIMIT_PRICE_PARAM)
    dpLimitPrice.value = price

    let dpStopPrice = this.createDynPropertyFromParameter(OrderEditBaseUtils.STOP_PRICE_PARAM)
    dpStopPrice.value = price

    this.updateParameters(new OrderEditUpdateData({
        limitPrice: dpLimitPrice,
        stopPrice: dpStopPrice
    }))
}

OCOOrderEdit.prototype.getBasePrice = function ()   // for #101148
{
    return this.limitPrice
}

OCOOrderEdit.prototype.skipParamForFlashing = function (paramName)   // при включенном positionSizing надо ли DynPropertyControl c paramName пропускать при подсветке лейблов (к примеру как Stop price не подсвечиваем у Stop limit-a)
{
    if (paramName == OrderEditBaseUtils.STOP_PRICE_PARAM)
        return true

    return false
}

// --------------------------------------------------------------------------------

// TODO. UGLY. It is used only in chart so far.
// Use already defined StopOrderEdit, StopLimitOrderEdit, LimitOrderEdit instances.
// Supports different stop/limit, buy/sell bound order pairs.
// TODO. Doesn't support StopLimit.
// NOTE:
// stopPrice - first order's price.
// limitPrice - second order's price.
export let OCOCustomOrdersEdit = function (data)
{
    OCOOrderEdit.call(this, data)

    this.firstOrderType = null
    this.secondOrderType = null

    this.firstOrderTif = null
    this.secondOrderTif = null

    this.firstOrderSide = null
    this.secondOrderSide = null
}

OCOCustomOrdersEdit.prototype = Object.create(OCOOrderEdit.prototype)

OCOCustomOrdersEdit.prototype.setOrdersTypes = function (firstOrderType, secondOrderType)
{
    let ordTypes = OrderType
    this.firstOrderType = firstOrderType
    this.secondOrderType = secondOrderType
}

OCOCustomOrdersEdit.prototype.setOrdersTifs = function (firstOrderTif, secondOrderTif)
{
    this.firstOrderTif = firstOrderTif
    this.secondOrderTif = secondOrderTif
}

OCOCustomOrdersEdit.prototype.setOrdersSides = function (firstOrderSide, secondOrderSide)
{
    this.firstOrderSide = firstOrderSide
    this.secondOrderSide = secondOrderSide
}

OCOCustomOrdersEdit.prototype.setOrdersPrices = function (firstOrderPrice, secondOrderPrice)
{
    this.stopPrice = secondOrderPrice
    this.limitPrice = firstOrderPrice

    // TODO. Validate.... Whatever. Details are at the top of OCOCustomOrdersEdit.
}

OCOCustomOrdersEdit.prototype.getDataForRequest = function ()
{
    let data = OCOOrderEdit.prototype.getDataForRequest.call(this)

    data.ocoCustomOrdersData = {
        firstOrderType: this.firstOrderType,
        secondOrderType: this.secondOrderType,

        firstOrderTif: this.firstOrderTif,
        secondOrderTif: this.secondOrderTif,

        firstOrderSide: this.firstOrderSide,
        secondOrderSide: this.secondOrderSide,

        firstOrderLimitPriceForStopLimit: this.firstOrderType === OrderType.StopLimit ? this.getLimitPriceWithDefaultOffset(this.limitPrice, this.firstOrderSide === OperationType.Buy) : null,
        secondOrderLimitPriceForStopLimit: this.secondOrderType === OrderType.StopLimit ? this.getLimitPriceWithDefaultOffset(this.stopPrice, this.secondOrderSide === OperationType.Buy) : null
    }

    return data
}